import * as React from "react";
import helpers from "../helpers/common";

const rootContext = React.createContext({
    authed: false,
    setAuthed: () => null,
    userData: {},
    setUserData: () => null,
    defaultCurrency: {},
    setDefaultCurrency: () => null,
    showLoader: false,
    setShowLoader: () => null,
    showNotification: false,
    setShowNotification: () => null,
    notificationMessage: '',
    setNotificationMessage: () => null,
    notificationTimeout: 6000,
    setNotificationTimeout: () => null,
    forcedMerchantID: null,
    setForcedMerchantID: () => null,
    setForcedMerchant: () => null,
    setForcedMerchantForReferral: () => null,
    removeForcedMerchant: () => null,
    notify: () => null,
    showError: () => null,
    showConfirm: () => null,
    showErrorDialog: false,
    setShowErrorDialog: () => null,
    errorDialogTitle: '',
    errorDialogMessage: '',
    showConfirmDialog: false,
    setShowConfirmDialog: () => null,
    confirmDialogTitle: '',
    confirmDialogMessage: '',
    handleConfirmDialogClose: () => null
});

function useRootHook() {
    const [authed, setAuthed] = React.useState(false);
    const [userData, setUserData] = React.useState(null);
    const [defaultCurrency, setDefaultCurrency] = React.useState(null);
    const [showLoader, setShowLoader] = React.useState(false);
    const [showNotification, setShowNotification] = React.useState(false);
    const [notificationMessage, setNotificationMessage] = React.useState('');
    const [notificationTimeout, setNotificationTimeout] = React.useState(6000);
    const [forcedMerchantID, setForcedMerchantID] = React.useState(null);
    const [showErrorDialog, setShowErrorDialog] = React.useState(false);
    const [errorDialogTitle, setErrorDialogTitle] = React.useState('');
    const [errorDialogMessage, setErrorDialogMessage] = React.useState('');
    const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
    const [confirmDialogTitle, setConfirmDialogTitle] = React.useState('');
    const [confirmDialogMessage, setConfirmDialogMessage] = React.useState('');
    const confirmDialogResolveRef = React.useRef(null);

    const notify = (text, timeout = 6000) => {
        setNotificationMessage(text)
        setNotificationTimeout(timeout)
        setShowNotification(true)
    }

    const showError = (message, title = 'Error') => {
        setErrorDialogTitle(title);
        setErrorDialogMessage(message);
        setShowErrorDialog(true);
    }

    const showConfirm = (message, title = 'Confirm', onConfirm) => {
        return new Promise((resolve) => {
            setConfirmDialogTitle(title);
            setConfirmDialogMessage(message);
            confirmDialogResolveRef.current = (confirmed) => {
                setShowConfirmDialog(false);
                confirmDialogResolveRef.current = null;
                if (confirmed && onConfirm) onConfirm();
                resolve(confirmed);
            };
            setShowConfirmDialog(true);
        });
    }
    
    const handleConfirmDialogClose = (confirmed) => {
        if (confirmDialogResolveRef.current) {
            confirmDialogResolveRef.current(confirmed);
        }
    }

    const setForcedMerchant = (merchantID, loginUserData = userData) => {
        if (
            ![helpers.roles.A, helpers.roles.S].includes(loginUserData.role)
            || !helpers.onlyDigitsRegex(merchantID)
        ) {
            return
        }

        helpers.forcedMerchantID = parseInt(merchantID)
        setForcedMerchantID(helpers.forcedMerchantID)
    }
    const setForcedMerchantForReferral = (merchantID, allowedMerchantIds = [], loginUserData = userData) => {
        if (loginUserData?.role !== helpers.roles.R || !helpers.onlyDigitsRegex(merchantID)) {
            return
        }
        const allowedSet = new Set((allowedMerchantIds || []).map((id) => parseInt(id, 10)))
        if (!allowedSet.has(parseInt(merchantID, 10))) {
            return
        }
        helpers.forcedMerchantID = parseInt(merchantID, 10)
        setForcedMerchantID(helpers.forcedMerchantID)
    }
    const removeForcedMerchant = (merchantID) => {
        helpers.forcedMerchantID = null
        setForcedMerchantID(helpers.forcedMerchantID)
        return true//for concatenating actions
    }

    return {
        authed,
        setAuthed,
        userData,
        setUserData,
        defaultCurrency,
        setDefaultCurrency,
        showLoader,
        setShowLoader,
        showNotification,
        setShowNotification,
        notificationMessage,
        setNotificationMessage,
        notificationTimeout,
        setNotificationTimeout,
        forcedMerchantID,
        setForcedMerchant,
        setForcedMerchantForReferral,
        removeForcedMerchant,
        notify,
        showError,
        showConfirm,
        showErrorDialog,
        setShowErrorDialog,
        errorDialogTitle,
        errorDialogMessage,
        showConfirmDialog,
        setShowConfirmDialog,
        confirmDialogTitle,
        confirmDialogMessage,
        handleConfirmDialogClose
    };
}

export function RootProvider({ children }) {
    const root = useRootHook();

    return <rootContext.Provider value={root}>{children}</rootContext.Provider>;
}

export default function RootConsumer() {
    return React.useContext(rootContext);
}

